import React, { useEffect, useContext, useState, useRef } from 'react';
import { AnimatedList } from 'react-animated-list';
import queryString from 'query-string';

import { Row, Col } from 'react-bootstrap';

import history from 'services/history';

import { Title, Header } from 'components/Common';

import SearchBar from 'components/SearchBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faCheck,
  faAngleUp,
  faAngleDown,
} from '@fortawesome/free-solid-svg-icons';
import {
  Filter,
  FilterHover,
  FilterWrapper,
} from 'components/SearchBar/styles';
import { Value } from 'components/Acessos';
import { ListingHeader, Ordering } from 'components/Listing/styles';
import { Pagination, AdvancedSearch } from '@cloudez/cloudez-design-system';

import EmptyListing from 'components/EmptyListing';
import { useDocTitle } from 'hooks/docTitle';
import { useLayout } from 'hooks/layout';
import InvoiceWarning from 'pages/Dashboard/InvoiceWarning';
import InvoicePlaceholder from './Placeholder';
import InvoiceCard from './InvoiceCard';

import {
  InvoiceListingContext,
  InvoiceListingProvider,
} from './_context/state';

import { DropdownList, ItemList } from './styles';

const Invoices = () => {
  const [dropdown, setDropdown] = useState<any>();
  const theme = useLayout();

  useDocTitle(`Faturas — ${theme.name}`);

  const { loading, invoices, getInvoices, count, previous, next, current } =
    useContext(InvoiceListingContext);

  const {
    page = 1,
    search: _search = '',
    status = '',
  } = queryString.parse(history.location.search);

  const [statusValue, setStatusValue] = useState<any>(Number(status) as any);
  const [orderValue, setOrderValue] = useState<any>();

  const [search, setSearch] = useState(
    queryString.parse(history.location.search),
  );

  const abortControllerRef = useRef<AbortController>(new AbortController());

  const getInvoicesCallback = (page, search, status, order) => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    getInvoices(page, search, status, order, abortControllerRef.current);

    const _query = {
      ...search,
      page,
      status,
    };

    if (!search) delete _query.search;
    if (!status) delete _query.search;
    if (page === 1 || page === '1') delete _query.page;

    const query = queryString.stringify(_query);
    history.replace(`/invoices?${query}`);
  };

  useEffect(() => {
    getInvoicesCallback(page, search, statusValue, orderValue);
    // eslint-disable-next-line
  }, []);

  const searchValue = searchValue => {
    getInvoicesCallback(page, searchValue, statusValue, orderValue);
  };

  const handleSetStatus = status => {
    setStatusValue(status);
    getInvoicesCallback(1, search, status, orderValue);
    setDropdown(false);
  };

  const order = column => {
    const order = {
      value: orderValue?.value === 2 ? 1 : orderValue?.value === 1 ? 0 : 2,
      column,
    };
    setOrderValue(order);
    getInvoicesCallback(1, searchValue, statusValue, order);
  };

  return (
    <>
      <Header>
        <Title>Faturas</Title>
      </Header>
      <InvoiceWarning />
      <SearchBar noInput>
        <AdvancedSearch
          style={{
            width: '400px',
          }}
          callback={searchValue}
          noResults={false}
          value={search}
          setValue={setSearch}
          fields={[
            {
              label: 'Qualquer campo',
              value: 'search',
            },
            {
              label: 'Id',
              value: 'id',
            },
            {
              label: 'Plano',
              value: 'plan_name',
            },
            {
              label: 'Nome',
              value: 'full_name',
            },
            {
              label: 'Email',
              value: 'email',
            },
          ]}
        />
        <FilterWrapper>
          <Filter>
            <FontAwesomeIcon
              icon={faDollarSign}
              color={statusValue ? theme.brand_primary_color : theme.interface4}
              onClick={() => {
                setDropdown(!dropdown);
              }}
            />
            <FilterHover>
              <Value>
                <span>Status</span>
              </Value>
            </FilterHover>
          </Filter>

          {dropdown && (
            <div
              style={{
                position: 'absolute',
                bottom: '22px',
                right: '120px',
                zIndex: 2,
              }}
            >
              <div
                style={{
                  position: 'fixed',
                  top: '0px',
                  right: '0px',
                  bottom: '0px',
                  left: '0px',
                  zIndex: 998,
                }}
                onClick={() => setDropdown(false)}
              />
              <DropdownList>
                <ItemList
                  selected={statusValue === 0}
                  onClick={() => handleSetStatus(0)}
                >
                  {statusValue === 0 && <FontAwesomeIcon icon={faCheck} />}
                  Todas
                </ItemList>

                <ItemList
                  selected={statusValue === 1}
                  onClick={() => handleSetStatus(1)}
                >
                  {statusValue === 1 && <FontAwesomeIcon icon={faCheck} />}
                  Pendentes
                </ItemList>

                <ItemList
                  selected={statusValue === 2}
                  onClick={() => handleSetStatus(2)}
                >
                  {statusValue === 2 && <FontAwesomeIcon icon={faCheck} />}
                  Atrasadas
                </ItemList>

                <ItemList
                  selected={statusValue === 3}
                  onClick={() => handleSetStatus(3)}
                >
                  {statusValue === 3 && <FontAwesomeIcon icon={faCheck} />}
                  Pagas
                </ItemList>
              </DropdownList>
            </div>
          )}
        </FilterWrapper>
      </SearchBar>

      {loading ? (
        <InvoicePlaceholder />
      ) : invoices?.length ? (
        <>
          <ListingHeader>
            <Row>
              <Col sm="1" className="d-none d-sm-flex">
                <p
                  onClick={() => {
                    order('id');
                  }}
                >
                  Id
                  <Ordering
                    status={orderValue?.column === 'id' ? orderValue?.value : 0}
                  >
                    <FontAwesomeIcon icon={faAngleUp} />
                    <FontAwesomeIcon icon={faAngleDown} />
                  </Ordering>
                </p>
              </Col>

              <Col xs sm md="4" lg="5" xl="5">
                <p
                  onClick={() => {
                    order('email');
                  }}
                >
                  Email
                  <Ordering
                    status={
                      orderValue?.column === 'email' ? orderValue?.value : 0
                    }
                  >
                    <FontAwesomeIcon icon={faAngleUp} />
                    <FontAwesomeIcon icon={faAngleDown} />
                  </Ordering>
                </p>
              </Col>

              <Col className="d-none d-sm-none d-md-flex">
                <p
                  onClick={() => {
                    order('value');
                  }}
                >
                  Valor
                  <Ordering
                    status={
                      orderValue?.column === 'value' ? orderValue?.value : 0
                    }
                  >
                    <FontAwesomeIcon icon={faAngleUp} />
                    <FontAwesomeIcon icon={faAngleDown} />
                  </Ordering>
                </p>
              </Col>

              <Col className="d-none d-sm-none d-md-flex">
                <p
                  onClick={() => {
                    order('expiration');
                  }}
                >
                  Vencimento
                  <Ordering
                    status={
                      orderValue?.column === 'expiration'
                        ? orderValue?.value
                        : 0
                    }
                  >
                    <FontAwesomeIcon icon={faAngleUp} />
                    <FontAwesomeIcon icon={faAngleDown} />
                  </Ordering>
                </p>
              </Col>

              <Col className="d-none d-sm-none d-md-flex">
                <p>Comprovante</p>
              </Col>

              <Col xl="1" md="2" sm="3" xs="4" className="d-none d-md-flex" />
            </Row>
          </ListingHeader>

          <AnimatedList initialAnimationDuration={800} animation="grow">
            {invoices.map(i => (
              <InvoiceCard invoice={i} key={i.id} />
            ))}
          </AnimatedList>

          <Pagination
            onChangePage={getInvoicesCallback}
            count={count}
            previous={previous}
            next={next}
            current={current}
            search={search}
            status={status.toString()}
            pageSize={10}
            invoices
            order={orderValue}
          />
        </>
      ) : (
        <EmptyListing>
          <p>Nenhuma fatura encontrada.</p>
        </EmptyListing>
      )}
    </>
  );
};

export default props => (
  <InvoiceListingProvider>
    <Invoices {...props} />
  </InvoiceListingProvider>
);
